import { YEARS_BEFORE } from "../configuration";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export const dayjsUTC = (...args) => dayjs.utc(...args);

export const MIN_DATE = dayjs.utc(
    Date.UTC(new Date().getUTCFullYear() - YEARS_BEFORE, 0, 1),
);

export const MAX_DATE = dayjs.utc(
    Date.UTC(new Date().getUTCFullYear() + YEARS_BEFORE, 0, 0),
);

export const monthToQuarter = (month) => {
    switch (month) {
        case 1:
        case 2:
        case 3:
            return 1;
        case 4:
        case 5:
        case 6:
            return 2;
        case 7:
        case 8:
        case 9:
            return 3;
        case 10:
        case 11:
        case 12:
            return 4;
        default:
            break;
    }
};

export const quarterToMonths = (quarter) => {
    switch (quarter) {
        case 1:
        case "Q1":
            return [1, 2, 3];
        case 2:
        case "Q2":
            return [4, 5, 6];
        case 3:
        case "Q3":
            return [7, 8, 9];
        case 4:
        case "Q4":
            return [10, 11, 12];
        default:
            break;
    }
};

export const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

export const months_abbreviations = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

export const weekDays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

export const dayTermination = ["th", "st", "nd", "rd"];

export const addDaySuffix = (dayNum, toLast = false) => {
    const v = dayNum % 100;
    if (toLast) return "last";
    else
        return (
            dayNum +
            (dayTermination[(v - 20) % 10] ||
                dayTermination[v] ||
                dayTermination[0])
        );
};

export const weeks = [1, 2, 3, 4, 5];

export const daysBetween = (from, to) =>
    (new Date(to).getTime() - new Date(from).getTime()) / (1000 * 3600 * 24);

export const getYearsBefore = () => {
    let years = [];
    let todayYear = new Date().getFullYear() + 1; // Actual Year + 1
    let auxYear = todayYear;
    while (auxYear >= todayYear - YEARS_BEFORE) {
        years.push(auxYear);
        auxYear -= 1;
    }
    return years;
};

export const xAxisYearMonth = () =>
    getYearsBefore()
        .reverse()
        .map((year) => {
            return months.map((month, index) => {
                return new Date(year, index + 1, 0).toISOString().slice(0, 7);
            });
        })
        .flat(1);

export const newDateUTC = (
    year = new Date().getUTCFullYear(),
    month = new Date().getUTCMonth(),
    day = new Date().getUTCDate(),
) => new Date(Date.UTC(year, month, day));

export const dateToUTC = (date, split = false) => {
    const dateTransform = new Date(date);
    if (dateTransform.getTime()) {
        // It's a valid date
        const year = dateTransform.getUTCFullYear();
        const month = dateTransform.getUTCMonth();
        const day = dateTransform.getUTCDate();
        return split
            ? { year, month, day }
            : new Date(Date.UTC(year, month, day));
    } else {
        // console.error("dateToUTC failed. Invalid date received: ", date);
        return -1;
    }
};

export const parsedDateToDayjs = (
    year = dayjs().year(),
    month = dayjs().month(),
    date = dayjs().date(),
) => dayjs({ year, month, date }).startOf("day");

/**
 * Given a date, returns the date on which the week starts.
 *  arg [string(yyyy-mm-dd)|new Date()] date: date on which the week start is calculated
 *  arg [int] startDay: Day considered the first day of the week, like "Sunday = 0, ... ,Saturday = 6"
 *  return new Date(): date considered the start of the week
 */
export const startOfWeekDate = (date, startDay = 1) => {
    date = new Date(date);
    if (startDay >= 0 && startDay <= 7 && date.getTime()) {
        const dayOfMonth = date.getUTCDate();
        const dayOfWeek = date.getUTCDay();
        const differenceInDays =
            dayOfWeek >= startDay
                ? dayOfWeek - startDay
                : dayOfWeek - startDay + 7;
        date.setUTCDate(dayOfMonth - differenceInDays);
        date.setUTCHours(0);
        date.setUTCMinutes(0);
        date.setUTCSeconds(0);
        date.setUTCMilliseconds(0);
        return date;
    } else if (startDay < 0 || startDay > 7) {
        console.error(
            "startOfWeekDate failed. Start of day must be >= 0 AND <= 7: ",
            startDay,
        );
        return -1;
    } else if (!date.getTime()) {
        console.error("startOfWeekDate failed. Invalid date received: ", date);
        return -1;
    } else {
        console.error("startOfWeekDate failed. Unknown error: ", {
            date,
            startDay,
        });
        return -1;
    }
};

export const getWeekNumberSince2000 = (date) => {
    let startDate = new Date("2000-01-01T00:00:00Z");
    // Get first Monday
    if (startDate.getDay() !== 1) {
        // If the first day is not Monday, get the next week
        startDate.setDate(startDate.getDate() + ((8 - startDate.getDay()) % 7));
    }

    const currentDate = new Date(date);

    const diff = currentDate - startDate;
    const weeks = Math.floor(diff / (1000 * 60 * 60 * 24 * 7)) + 1;
    return weeks;
};

export const getWeekYear = (date) => {
    const d = new Date(
        Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
    );

    const day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - day);

    const year = d.getUTCFullYear();
    const yearStart = new Date(Date.UTC(year, 0, 1));
    const week = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);

    return {
        week,
        year,
        label: `Week #${week} ${year}`,
        sort: year * 100 + week,
    };
};

export const getDateFromWeekNumberSince2000 = (weekNumber, dayOfWeek = 0) => {
    // Start at first week from 2000s
    let startDate = new Date("2000-01-01T00:00:00Z");
    if (startDate.getDay() !== 1) {
        startDate.setDate(startDate.getDate() + ((8 - startDate.getDay()) % 7));
    }

    const millisecondsPerWeek = 1000 * 60 * 60 * 24 * 7;
    let day = new Date(
        startDate.getTime() + millisecondsPerWeek * (weekNumber - 1),
    );

    if (dayOfWeek < 0 || dayOfWeek > 6) {
        throw new Error("dayOfWeek must be between 0 (Monday) and 6 (Sunday)");
    }

    day.setDate(day.getDate() + dayOfWeek);

    return day.toISOString().slice(0, 10);
};

export const formatDate = (date, showDay = true) => {
    if (dayjs.isDayjs(date))
        return dayjs(date).format(`${showDay ? "DD " : ""}MMM YYYY`);
    else {
        return new Date(date).toLocaleDateString("en-IE", {
            year: "numeric",
            month: "short",
            ...(showDay && { day: "2-digit" }),
        });
    }
};

export const formatDateRange = (fromDateStr, toDateStr, locale = "en-IE") => {
    const from = new Date(fromDateStr);
    const to = new Date(toDateStr);

    const sameDay = from.toDateString() === to.toDateString();
    const sameMonth =
        from.getMonth() === to.getMonth() &&
        from.getFullYear() === to.getFullYear();
    const sameYear = from.getFullYear() === to.getFullYear();

    const weekday = { weekday: "short" };
    const dayMonth = { day: "numeric", month: "short" };
    const fullDate = { day: "numeric", month: "short", year: "numeric" };

    const format = (date, opts) =>
        date.toLocaleDateString(locale, { ...weekday, ...opts });

    if (sameDay) {
        return format(from, fullDate); // e.g., Mon, 10 Nov 2025
    }
    // else if (sameMonth) {
    //     return `${format(from, { day: "numeric" })} - ${format(
    //         to,
    //         dayMonth
    //     )} ${from.getFullYear()}`; // e.g., Mon, 10 - Wed, 15 Nov 2025
    // } else if (sameYear) {
    //     return `${format(from, dayMonth)} - ${format(to, {
    //         ...dayMonth,
    //         year: "numeric",
    //     })}`; // e.g., Mon, 28 Nov - Tue, 2 Dec 2025
    // }
    else {
        return `${format(from, fullDate)} → ${format(to, fullDate)}`; // e.g., Mon, 28 Dec 2025 - Thu, 2 Jan 2026
    }
};

export const formatDayjs = (date, showDay = true) =>
    dayjs(date).format(`${showDay ? "DD " : ""}MMM YYYY`);

export const subtractOneYear = (dateString, adjustLY = false) => {
    const date = adjustLY
        ? adjustLeapYear(new Date(dateString))
        : new Date(dateString);
    date.setFullYear(date.getFullYear() - 1);
    return formatDate(date);
};

export const subtractOneMonth = (dateString) => {
    const date = new Date(dateString);
    date.setMonth(date.getMonth() - 1);
    return formatDate(date);
};
export const subtractOneWeek = (dateString) => {
    const date = new Date(dateString);
    date.setDate(date.getDate() - 7);
    return formatDate(date);
};

// Adjust leap year when a "previous year" comparison is being made by avoiding 29th Feb turning into 1st of March
export const adjustLeapYear = (originalDate) => {
    if (dayjs.isDayjs(originalDate)) {
        if (originalDate.month() === 1 && originalDate.date() === 29) {
            originalDate.date(28);
        }
    } else {
        if (
            originalDate.getUTCMonth() === 1 &&
            originalDate.getUTCDate() === 29
        ) {
            originalDate.setUTCDate(28);
        }
    }

    return originalDate;
};

export const monthlyIntervals = (fromDay, toDay) => {
    const fromDate = dateToUTC(fromDay);
    const toDate = dateToUTC(toDay);
    const intervals = [];

    while (fromDate <= toDate) {
        const yearMonth = dateToUTC(fromDate).toISOString().substr(0, 7);
        intervals.push(yearMonth);
        fromDate.setUTCDate(1); // Set to the first day of the next month
        fromDate.setUTCMonth(fromDate.getUTCMonth() + 1); // Increment the month
    }

    return intervals;
};

export const weeklyIntervals = (fromDay, toDay) => {
    const fromDate = startOfWeekDate(fromDay);
    const toDate = dateToUTC(toDay);
    const intervals = [];

    while (fromDate <= toDate) {
        let weekStart = fromDate;
        const yearMonthWeekStart = dateToUTC(weekStart)
            .toISOString()
            .substr(0, 10);

        intervals.push(
            dateToUTC(yearMonthWeekStart).toISOString().substr(0, 10),
        );

        fromDate.setUTCDate(fromDate.getUTCDate() + 7); // Move to next week
        weekStart = startOfWeekDate(fromDate);
    }

    return intervals;
};

export const dailyIntervals = (fromDay, toDay) => {
    const fromDate = dateToUTC(fromDay);
    const toDate = dateToUTC(toDay);
    const intervals = [];

    while (fromDate <= toDate) {
        const yearMonthDay = dateToUTC(fromDate).toISOString().substr(0, 10);
        intervals.push(yearMonthDay);
        fromDate.setUTCDate(fromDate.getUTCDate() + 1);
    }

    return intervals;
};
